import React, {useEffect, useRef, memo, startTransition, Suspense} from "react"
import {breakpoints} from "@next/ux-fabric"
import {useScrollTrigger} from "@mui/material"
import {useTemplatedComponent} from "@next/templating"
import {MANIFEST} from "@manifest/header"
import {
    useCountrySelectorOpenObservable,
    useModalsCloseObservable,
    useCountrySelectorRedirectToAlternativeLanguageObservable,
} from "@next/eventservice"
import connect from "./connect"
import {Wrapper} from "./component"
import CountryLangSelectorButton from "../CountryLangSelectorButton"
import type {Country as CountryType} from "../../models/countryselector"
import {removeDeptFromSessionStorage} from "../../utils/removeDeptFromSessionStorage"
import {useMediaQuery} from "../../hooks/useMediaQuery"

export type DefaultTemplatePropType = {
    locationText: string
    ROWLinkUrl: string
    ROWText: string
    ROWLinkText: string
    languageText: string
    selectedCountry: CountryType | null
    selectedLanguage: string | null
    selectLanguage: (language: string) => void
    shopNowText: string
    headerTitle: string
    closeText: string
    onCTAClick: () => void
    closePanel: () => void
    isOpeningFromBottom?: boolean
    isInternationalCountry: boolean
    stayText: string
    textAlignment: string
    isOpenCountrySelectorDrawer: boolean
    keepMounted: boolean
    disablePortal: boolean
    hideBackdrop: boolean
    disableScrollLock: boolean
    isShowOverlay: boolean
    territory: string
}

export type CountryLangSelectorType = {
    territory: string
    textAlignment: string
    iconUrl: string
    isEnableCountrySelectorButton: boolean
    isShowOverlay: boolean
    isOpenCountrySelectorDrawer: boolean
    isEnableCountrySelectorDrawer: boolean
    isInternationalCountry: boolean
    selectedLanguage: string | null
    selectedCountry: CountryType | null
    itemCount: number
    shopNowOnClick: (itemCount: number) => void
    openDrawer: (isShowOverlay: boolean) => void
    closePanel: () => void
    deleteRecentQueries: () => void
    selectLanguage: (language: string) => void
    selectDefaultCountry: (countryCode: string) => void
    getCountriesListThunk: () => void
    isLoaded: boolean
    ROWLinkUrl: string
    redirectPageToAlternativeLanguage: () => void
    text: any
    isOpeningFromBottom?: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    applyMarginCompensation?: boolean
    isSecure?: boolean
}

export const CountryLangSelector = memo(
    ({
        territory,
        textAlignment,
        iconUrl,
        isOpenCountrySelectorDrawer,
        isEnableCountrySelectorDrawer,
        isEnableCountrySelectorButton,
        selectDefaultCountry,
        isShowOverlay,
        isInternationalCountry,
        selectedLanguage,
        selectedCountry,
        itemCount,
        shopNowOnClick,
        openDrawer,
        selectLanguage,
        closePanel,
        deleteRecentQueries,
        getCountriesListThunk,
        isLoaded,
        ROWLinkUrl,
        redirectPageToAlternativeLanguage,
        text,
        isOpeningFromBottom,
        templating,
        applyMarginCompensation,
        isSecure = false,
    }: CountryLangSelectorType) => {
        const isMobile = useMediaQuery(`(max-width:${breakpoints.values.md}px)`)
        const trigger = useScrollTrigger({
            threshold: 0,
        })
        const ComponentName = useTemplatedComponent(templating, MANIFEST)

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const templateProps = {
            ...text,
            selectedLanguage,
            selectedCountry,
            selectLanguage,
            ROWLinkUrl,
            closePanel,
            isInternationalCountry,
            textAlignment,
            isOpeningFromBottom,
            isOpenCountrySelectorDrawer,
            isShowOverlay,
            keepMounted: false,
            disablePortal: true,
            hideBackdrop: !isShowOverlay,
            disableScrollLock: isMobile,
        }

        const hasGetCountriesRequestSent = useRef<boolean>(false)
        useEffect(() => {
            if (trigger && isMobile) {
                closePanel()
            }
        }, [isMobile, trigger, closePanel])

        useEffect(() => {
            if (isOpenCountrySelectorDrawer && !isLoaded && !hasGetCountriesRequestSent.current) {
                hasGetCountriesRequestSent.current = true
                startTransition(() => {
                    getCountriesListThunk()
                })
            }
        }, [isOpenCountrySelectorDrawer, isLoaded, getCountriesListThunk, hasGetCountriesRequestSent])

        useCountrySelectorOpenObservable(data => {
            startTransition(() => {
                selectDefaultCountry(data.isoCountryCode)
                openDrawer(false)
            })
        })

        useCountrySelectorRedirectToAlternativeLanguageObservable(() => {
            startTransition(() => {
                redirectPageToAlternativeLanguage()
            })
        })

        useModalsCloseObservable(() => {
            startTransition(() => {
                closePanel()
            })
        })

        const onCTAClick = () => {
            removeDeptFromSessionStorage()
            shopNowOnClick(itemCount)
            deleteRecentQueries()
        }

        const buttonContainerProps = {
            territory,
            iconUrl,
            openDrawer,
            templating,
            applyMarginCompensation,
            isSecure,
        }

        return (
            <>
                {isEnableCountrySelectorButton && <CountryLangSelectorButton {...buttonContainerProps} />}
                {isEnableCountrySelectorDrawer && ComponentName && (
                    <Wrapper id="header-country-selector-wrapper">
                        <Suspense>
                            <ComponentName {...templateProps} onCTAClick={onCTAClick} />
                        </Suspense>
                    </Wrapper>
                )}
            </>
        )
    },
)
CountryLangSelector.displayName = "CountryLangSelector"

export default connect(CountryLangSelector)
